import React, { useEffect, useState } from 'react';
import { apiRequest } from '../api/api';
import styles from './CustomerCRM.module.css';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer } from 'recharts';
import DataFallback from '../components/feedback/DataFallback';

function CustomerCRM() {
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  
  // Pagination and filtering states
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const [limit, setLimit] = useState(10);

  // Fetch customers with pagination and filtering
  const fetchCustomers = (page = 1, search = '', status = '', businessType = '') => {
    setLoading(true);
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (businessType) params.append('business_type', businessType);
    
    const customersUrl = `/api/core/offline-crm/customers/?${params.toString()}`;
    
    Promise.all([
      apiRequest({ method: 'GET', url: customersUrl }),
      apiRequest({ method: 'GET', url: '/api/core/offline-crm/dashboard/' })
    ])
      .then(([customersData, analyticsData]) => {
        console.log('API responses received:', { customersData, analyticsData });
        
        // Handle customers data - check if it's real API response or mock response
        if (customersData) {
          if (customersData.customers && customersData.pagination) {
            // Real API response structure
            console.log('Using real API response structure');
            setCustomers(customersData.customers);
            setPagination(customersData.pagination);
          } else if (customersData.success && customersData.data) {
            // Mock API response structure
            console.log('Using mock API response structure');
            setCustomers(customersData.data.customers);
            setPagination(customersData.data.pagination);
          } else if (Array.isArray(customersData)) {
            // Direct array response
            console.log('Using direct array response');
            setCustomers(customersData);
          }
        }
        
        // Handle analytics data - check if it's real API response or mock response
        if (analyticsData) {
          if (analyticsData.customer_stats || analyticsData.order_stats || analyticsData.financial_stats) {
            // Real API response structure
            console.log('Using real analytics API response');
            setAnalytics(analyticsData);
          } else if (analyticsData.success) {
            // Mock API response structure
            const analyticsToUse = analyticsData.data || analyticsData.analytics;
            console.log('Using mock analytics API response');
            setAnalytics(analyticsToUse);
          }
        }
        
        console.log('Analytics structure:', JSON.stringify(analyticsData, null, 2));
      })
      .catch((err) => {
        console.error('API call error:', err);
        setError('Failed to load customer data');
      })
      .finally(() => {
        console.log('API calls completed, setting loading to false');
        setLoading(false);
      });
  };

  useEffect(() => {
    console.log('CustomerCRM useEffect triggered - Making API calls...');
    fetchCustomers(currentPage, searchTerm, statusFilter, businessTypeFilter);
  }, [currentPage, limit]);

  // Handle search and filter changes
  const handleSearch = () => {
    setCurrentPage(1);
    fetchCustomers(1, searchTerm, statusFilter, businessTypeFilter);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'businessType') {
      setBusinessTypeFilter(value);
    }
    setCurrentPage(1);
    fetchCustomers(1, searchTerm, filterType === 'status' ? value : statusFilter, filterType === 'businessType' ? value : businessTypeFilter);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setBusinessTypeFilter('');
    setCurrentPage(1);
    fetchCustomers(1, '', '', '');
  };

  if (loading) return (
    <div className={styles.page}>
      <p>Loading Customer CRM Dashboard...</p>
    </div>
  );
  if (error) return (
    <div className={styles.page}>
      <DataFallback
        variant="pane"
        error={typeof error === 'string' ? { message: error, retryable: true } : error}
        onRetry={() => window.location.reload()}
      />
    </div>
  );

  // Debug logging
  console.log('Current analytics state:', analytics);
  console.log('Current customers state:', customers);

  // Use analytics data if available, otherwise calculate from customers
  const totalCustomers = analytics?.customer_stats?.total_customers || customers.length;
  console.log('Analytics customer_stats:', analytics?.customer_stats);
  console.log('Analytics financial_stats:', analytics?.financial_stats);
  
  const averageLTV = analytics?.financial_stats?.avg_order_value || 
    (customers.reduce((sum, cust) => sum + parseFloat(cust.credit_limit || 0), 0) / totalCustomers).toFixed(2);

  console.log('Calculated totalCustomers:', totalCustomers);
  console.log('Calculated averageLTV:', averageLTV);

  // Status distribution from analytics or calculated  
  const statusData = analytics?.order_stats?.order_statuses?.map(item => ({
    status: item.status,
    value: item.count
  })) || customers.reduce((acc, cust) => {
    const status = cust.is_active ? 'Active' : 'Inactive';
    const existing = acc.find(item => item.status === status);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ status, value: 1 });
    }
    return acc;
  }, []);

  // Business type distribution
  const businessTypeData = analytics?.customer_stats?.customer_types?.map(item => ({
    type: item.account_type,
    value: item.count,
    revenue: 0 // Backend doesn't provide revenue per type yet
  })) || customers.reduce((acc, cust) => {
    const type = cust.account_type || 'Unknown';
    const existing = acc.find(item => item.type === type);
    if (existing) {
      existing.value += 1;
      existing.revenue += parseFloat(cust.credit_limit || 0);
    } else {
      acc.push({ type, value: 1, revenue: parseFloat(cust.credit_limit || 0) });
    }
    return acc;
  }, []);

  // Monthly revenue from analytics - use recent orders for now
  const monthlyRevenueData = analytics?.recent_activity?.recent_orders ? 
    analytics.recent_activity.recent_orders.slice(0, 6).map((order, index) => ({
      month: `Month ${index + 1}`,
      revenue: parseFloat(order.total_amount),
      orders: 1
    })) : [];

  // Customer segments - use account types from backend data
  const segmentData = analytics?.customer_stats?.customer_types?.map(item => ({
    segment: item.account_type,
    value: item.count
  })) || [];

  // Interest data - use industries as interests
  const interestData = customers.reduce((acc, cust) => {
    const interest = cust.industry || 'Unknown';
    const existing = acc.find(item => item.interest === interest);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ interest, value: 1 });
    }
    return acc;
  }, []);

  // Activity levels based on registration date (last 30 days for new customers)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const recentCustomers = analytics?.customer_stats?.new_customers_this_month || 
    customers.filter(cust => 
      new Date(cust.created_at) >= thirtyDaysAgo
    ).length;
  const olderCustomers = totalCustomers - recentCustomers;

  const activityData = [
    { status: 'New This Month', value: recentCustomers },
    { status: 'Established', value: olderCustomers },
  ];

  // Payment status from analytics - use order statuses
  const paymentStatusData = analytics?.order_stats?.order_statuses?.map(item => ({
    status: item.status,
    amount: 0, // Backend doesn't provide amounts per status yet
    count: item.count
  })) || [];

  // Customer details handler
  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerDetails(true);
  };

  return (
    <div className={styles.page}>
      {/* Search and Filter Controls */}
      <div className={styles.controlPanel}>
        <h2>Customer CRM Dashboard</h2>
        <div className={styles.filterSection}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className={styles.searchInput}
            />
            <button onClick={handleSearch} className={styles.searchButton}>
              Search
            </button>
          </div>
          
          <div className={styles.filters}>
            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="lead">Lead</option>
              <option value="vip">VIP</option>
            </select>
            
            <select
              value={businessTypeFilter}
              onChange={(e) => handleFilterChange('businessType', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">All Business Types</option>
              <option value="distributor">Distributor</option>
              <option value="wholesaler">Wholesaler</option>
              <option value="retailer">Retailer</option>
            </select>
            
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className={styles.filterSelect}
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
            
            <button onClick={clearFilters} className={styles.clearButton}>
              Clear Filters
            </button>
          </div>
        </div>
        
        {/* Pagination Info */}
        {pagination && (
          <div className={styles.paginationInfo}>
            Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total_count)} of {pagination.total_count} customers
          </div>
        )}
      </div>

      <div className={styles.crmCard}>
        {/* Overview panel */}
        <div className={styles.panel}>
          <h3>Overview</h3>
          <p><strong>Total Customers:</strong> {totalCustomers}</p>
          <p><strong>Active Customers:</strong> {analytics?.customer_stats?.active_customers || customers.filter(c => c.is_active).length}</p>
          <p><strong>Average Credit Limit:</strong> ${averageLTV}</p>
          {analytics?.financial_stats && (
            <>
              <p><strong>Total Revenue:</strong> ${parseFloat(analytics.financial_stats.total_revenue).toLocaleString()}</p>
              <p><strong>Average Order Value:</strong> ${parseFloat(analytics.financial_stats.avg_order_value).toLocaleString()}</p>
            </>
          )}
          {analytics?.order_stats && (
            <>
              <p><strong>Total Orders:</strong> {analytics.order_stats.total_orders}</p>
              <p><strong>Pending Orders:</strong> {analytics.order_stats.pending_orders}</p>
            </>
          )}
        </div>

        {/* Activity Levels panel */}
        <div className={styles.panel}>
          <h3>Activity Levels</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={activityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Business Type Distribution panel */}
        <div className={styles.panel}>
          <h3>Business Type Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={businessTypeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value, name) => [
                name === 'value' ? `${value} customers` : `$${parseFloat(value).toLocaleString()}`,
                name === 'value' ? 'Count' : 'Revenue'
              ]} />
              <Bar dataKey="value" fill="#8884d8" />
              <Bar dataKey="revenue" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Revenue panel */}
        {monthlyRevenueData.length > 0 && (
          <div className={styles.panel}>
            <h3>Monthly Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => [`$${parseFloat(value).toLocaleString()}`, 'Revenue']} />
                <Line type="monotone" dataKey="revenue" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Customer Interests panel */}
        {interestData.length > 0 && (
          <div className={styles.panel}>
            <h3>Product Interests</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={interestData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="interest" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Payment Status panel */}
        {paymentStatusData.length > 0 && (
          <div className={styles.panel}>
            <h3>Payment Status</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={paymentStatusData} dataKey="value" nameKey="status" cx="50%" cy="50%" outerRadius={80}>
                  {paymentStatusData.map((entry, idx) => (
                    <Cell key={`payment-${idx}`} fill={["#28a745", "#ffc107", "#dc3545"][idx % 3]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name, props) => [
                  `${value} orders ($${parseFloat(props.payload.amount).toLocaleString()})`,
                  name
                ]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Status Distribution panel */}
        <div className={styles.panel}>
          <h3>Status Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="status" cx="50%" cy="50%" outerRadius={80}>
                {statusData.map((entry, idx) => (
                  <Cell key={`status-${idx}`} fill={["#8884d8", "#82ca9d", "#ffc658", "#ff8042"][idx % 4]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Customer Segments panel */}
        {segmentData.length > 0 && (
          <div className={styles.panel}>
            <h3>Customer Segments</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={segmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="segment" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Customer List panel */}
        <div className={styles.panel} style={{ gridColumn: 'span 2' }}>
          <h3>Customer List</h3>
          <div className={styles.customerGrid}>
            {customers.slice(0, 6).map((customer) => (
              <div 
                key={customer.id} 
                className={styles.customerCard}
                onClick={() => handleCustomerClick(customer)}
              >
                <h4>{customer.name}</h4>
                <p><strong>Status:</strong> <span className={`${styles.status} ${customer.is_active ? styles.active : styles.inactive}`}>
                  {customer.is_active ? 'Active' : 'Inactive'}
                </span></p>
                <p><strong>Type:</strong> {customer.account_type}</p>
                <p><strong>Industry:</strong> {customer.industry}</p>
                <p><strong>Account #:</strong> {customer.account_number}</p>
                <p><strong>Credit Limit:</strong> ${parseFloat(customer.credit_limit || 0).toLocaleString()}</p>
                <p><strong>Payment Terms:</strong> {customer.payment_terms}</p>
                <p><strong>Email:</strong> {customer.primary_email}</p>
                <p><strong>Phone:</strong> {customer.primary_phone}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Orders panel */}
        {analytics?.recent_activity?.recent_orders && (
          <div className={styles.panel}>
            <h3>Recent Orders</h3>
            <div className={styles.recentOrdersList}>
              {analytics.recent_activity.recent_orders.slice(0, 5).map((order) => (
                <div key={order.id} className={styles.orderItem}>
                  <h5>{order.order_number}</h5>
                  <p><strong>Customer:</strong> {order.customer_name}</p>
                  <p><strong>Status:</strong> <span className={`${styles.status} ${styles[order.status?.toLowerCase()]}`}>
                    {order.status}
                  </span></p>
                  <p><strong>Amount:</strong> ${parseFloat(order.total_amount).toLocaleString()}</p>
                  <p><strong>Date:</strong> {new Date(order.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <div className={styles.paginationControls}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!pagination.has_previous}
            className={styles.pageButton}
          >
            Previous
          </button>
          
          <div className={styles.pageNumbers}>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`${styles.pageButton} ${page === currentPage ? styles.activePage : ''}`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!pagination.has_next}
            className={styles.pageButton}
          >
            Next
          </button>
        </div>
      )}

      {/* Customer Details Modal */}
      {showCustomerDetails && selectedCustomer && (
        <div className={styles.modal} onClick={() => setShowCustomerDetails(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{selectedCustomer.company_name}</h2>
              <button className={styles.closeButton} onClick={() => setShowCustomerDetails(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.customerDetails}>
                <div className={styles.detailSection}>
                  <h3>Company Information</h3>
                  <p><strong>Business Type:</strong> {selectedCustomer.business_type}</p>
                  <p><strong>Industry:</strong> {selectedCustomer.industry}</p>
                  <p><strong>Annual Revenue:</strong> ${parseFloat(selectedCustomer.annual_revenue || 0).toLocaleString()}</p>
                  <p><strong>Employee Count:</strong> {selectedCustomer.employee_count}</p>
                  <p><strong>Address:</strong> {selectedCustomer.address}</p>
                  <p><strong>Phone:</strong> {selectedCustomer.phone}</p>
                  <p><strong>Email:</strong> {selectedCustomer.email}</p>
                  {selectedCustomer.website && (
                    <p><strong>Website:</strong> <a href={selectedCustomer.website} target="_blank" rel="noopener noreferrer">{selectedCustomer.website}</a></p>
                  )}
                </div>

                <div className={styles.detailSection}>
                  <h3>Account Details</h3>
                  <p><strong>Status:</strong> <span className={`${styles.status} ${styles[selectedCustomer.status?.name?.toLowerCase()]}`}>
                    {selectedCustomer.status?.name}
                  </span></p>
                  <p><strong>Credit Limit:</strong> ${parseFloat(selectedCustomer.credit_limit || 0).toLocaleString()}</p>
                  <p><strong>Payment Terms:</strong> {selectedCustomer.payment_terms}</p>
                  <p><strong>Registration Date:</strong> {selectedCustomer.registration_date}</p>
                  <p><strong>Last Order Date:</strong> {selectedCustomer.last_order_date || 'Never'}</p>
                </div>

                <div className={styles.detailSection}>
                  <h3>Order Summary</h3>
                  <p><strong>Total Orders:</strong> {selectedCustomer.total_orders}</p>
                  <p><strong>Total Order Value:</strong> ${parseFloat(selectedCustomer.total_order_value || 0).toLocaleString()}</p>
                </div>

                {selectedCustomer.primary_contacts && selectedCustomer.primary_contacts.length > 0 && (
                  <div className={styles.detailSection}>
                    <h3>Primary Contacts</h3>
                    {selectedCustomer.primary_contacts.map((contact) => (
                      <div key={contact.id} className={styles.contactCard}>
                        <p><strong>{contact.first_name} {contact.last_name}</strong></p>
                        <p>{contact.position}</p>
                        <p>Email: {contact.email}</p>
                        <p>Phone: {contact.phone}</p>
                      </div>
                    ))}
                  </div>
                )}

                {selectedCustomer.segments && selectedCustomer.segments.length > 0 && (
                  <div className={styles.detailSection}>
                    <h3>Segments</h3>
                    <div className={styles.tagList}>
                      {selectedCustomer.segments.map((segment, idx) => (
                        <span key={idx} className={styles.tag}>{segment}</span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCustomer.interests && selectedCustomer.interests.length > 0 && (
                  <div className={styles.detailSection}>
                    <h3>Interests</h3>
                    <div className={styles.tagList}>
                      {selectedCustomer.interests.map((interest, idx) => (
                        <span key={idx} className={styles.tag}>{interest}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerCRM;
